import React, { useState, useMemo } from 'react';
import { ChevronRight, ChevronDown, Folder, FolderOpen, Download, Eye } from 'lucide-react';
import './FileTree.css';

export interface FileTreeDocument {
  docId: string;
  fileName: string;
  fileType: string;
  fileSize: number;
  uploadDate: string;
  uploaderName: string;
}

interface TreeNode {
  name: string;
  type: 'file' | 'folder';
  path: string;
  children?: Map<string, TreeNode>;
  document?: FileTreeDocument;
}

interface FileTreeProps {
  documents: FileTreeDocument[];
  formatFileSize: (bytes: number) => string;
  formatDate: (dateString: string) => string;
  onDownload: (docId: string) => void;
  onPreview: (docId: string) => void;
}

const buildFileTree = (documents: FileTreeDocument[]): Map<string, TreeNode> => {
  const root = new Map<string, TreeNode>();

  documents.forEach((doc) => {
    const cleanFileName = doc.fileName.replace(/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}-/i, '');
    const parts = cleanFileName.split('/');
    
    let currentLevel = root;
    let currentPath = '';

    parts.forEach((part, index) => {
      currentPath = currentPath ? `${currentPath}/${part}` : part;
      const isFile = index === parts.length - 1;

      if (!currentLevel.has(part)) {
        currentLevel.set(part, {
          name: part,
          type: isFile ? 'file' : 'folder',
          path: currentPath,
          children: isFile ? undefined : new Map(),
          document: isFile ? doc : undefined,
        });
      }

      if (!isFile) {
        currentLevel = currentLevel.get(part)!.children!;
      }
    });
  });

  return root;
};

const FileTreeNode: React.FC<{
  node: TreeNode;
  level: number;
  formatFileSize: (bytes: number) => string;
  formatDate: (dateString: string) => string;
  onDownload: (docId: string) => void;
  onPreview: (docId: string) => void;
}> = ({ node, level, formatFileSize, formatDate, onDownload, onPreview }) => {
  const [isExpanded, setIsExpanded] = useState(false);

  const toggleExpand = (e: React.MouseEvent) => {
    if (node.type === 'folder') {
      e.stopPropagation();
      setIsExpanded(!isExpanded);
    }
  };

  const handlePreview = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (node.document) {
      onPreview(node.document.docId);
    }
  };

  const handleDownload = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (node.document) {
      onDownload(node.document.docId);
    }
  };

  const childrenArray = node.children ? Array.from(node.children.values()) : [];
  
  // Sort: folders first, then files, both alphabetically
  childrenArray.sort((a, b) => {
    if (a.type === b.type) {
      return a.name.localeCompare(b.name);
    }
    return a.type === 'folder' ? -1 : 1;
  });

  return (
    <div className="file-tree-node">
      <div
        className={`tree-item ${node.type === 'file' ? 'file-item' : 'folder-item'} level-${level}`}
        onClick={toggleExpand}
        style={{ paddingLeft: `${level * 20 + 8}px` }}
      >
        <div className="tree-item-content">
          {node.type === 'folder' && (
            <>
              <span className="expand-icon">
                {isExpanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
              </span>
              {isExpanded ? <FolderOpen size={16} className="folder-icon" /> : <Folder size={16} className="folder-icon" />}
            </>
          )}
          <span className="tree-item-name">{node.name}</span>
        </div>
        
        {node.type === 'file' && node.document && (
          <div className="file-metadata">
            <span className="file-type">{(() => {
              // Extract file extension from fileName (source of truth)
              const fileName = node.document.fileName || '';
              const parts = fileName.toLowerCase().split('.');
              const extension = parts.length > 1 ? parts[parts.length - 1] : 'file';
              return extension.toUpperCase();
            })()}</span>
            <span className="file-size">{formatFileSize(node.document.fileSize || 0)}</span>
            <span className="file-date">{formatDate(node.document.uploadDate)}</span>
            <div className="file-actions">
              <button 
                onClick={handlePreview} 
                className="file-action-btn"
                title="Preview"
              >
                <Eye size={16} />
              </button>
              <button 
                onClick={handleDownload} 
                className="file-action-btn"
                title="Download"
              >
                <Download size={16} />
              </button>
            </div>
          </div>
        )}
      </div>

      {node.type === 'folder' && isExpanded && childrenArray.length > 0 && (
        <div className="tree-children">
          {childrenArray.map((child) => (
            <FileTreeNode
              key={child.path}
              node={child}
              level={level + 1}
              formatFileSize={formatFileSize}
              formatDate={formatDate}
              onDownload={onDownload}
              onPreview={onPreview}
            />
          ))}
        </div>
      )}
    </div>
  );
};

const FileTree: React.FC<FileTreeProps> = ({
  documents,
  formatFileSize,
  formatDate,
  onDownload,
  onPreview,
}) => {
  const fileTree = useMemo(() => buildFileTree(documents), [documents]);
  
  const rootNodes = Array.from(fileTree.values()).sort((a, b) => {
    if (a.type === b.type) {
      return a.name.localeCompare(b.name);
    }
    return a.type === 'folder' ? -1 : 1;
  });

  if (documents.length === 0) {
    return <div className="empty-state">No documents available</div>;
  }

  return (
    <div className="file-tree-container">
      <div className="file-tree-header">
        <span className="total-count">{documents.length} document{documents.length !== 1 ? 's' : ''}</span>
      </div>
      <div className="file-tree-list">
        {rootNodes.map((node) => (
          <FileTreeNode
            key={node.path}
            node={node}
            level={0}
            formatFileSize={formatFileSize}
            formatDate={formatDate}
            onDownload={onDownload}
            onPreview={onPreview}
          />
        ))}
      </div>
    </div>
  );
};

export default FileTree;

